import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Bookmark, Trash2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { listPresets, createPreset, deletePreset } from '../../api/mapping-presets-api'
import type { MappingConfig } from '../../lib/mapping-types'

export function PresetBar({
  projectId,
  formId,
  config,
  onApply,
}: {
  projectId: string
  formId: string
  config: MappingConfig
  onApply: (config: MappingConfig) => void
}) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')

  const { data: presets = [] } = useQuery({
    queryKey: ['import-presets', formId],
    queryFn: () => listPresets(formId),
    enabled: !!formId,
  })

  const saveMutation = useMutation({
    mutationFn: () =>
      createPreset({ projectId, formId, userId: user!.id, name: name.trim(), visibility: 'private', config }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['import-presets', formId] })
      setSaving(false)
      setName('')
      toast({ title: 'نگاشت ذخیره شد' })
    },
    onError: (e) => toast({ variant: 'destructive', title: 'خطا', description: (e as Error).message }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePreset(id, user!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['import-presets', formId] }),
  })

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Bookmark className="w-4 h-4 text-muted-foreground shrink-0" />
      {presets.length > 0 && (
        <Select
          value=""
          onValueChange={(id) => {
            const p = presets.find((x) => x.id === id)
            if (p) {
              onApply(p.config)
              toast({ title: `نگاشت «${p.name}» اعمال شد` })
            }
          }}
        >
          <SelectTrigger className="h-8 text-sm w-56"><SelectValue placeholder="بارگذاری نگاشت ذخیره‌شده" /></SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} {p.visibility === 'project' && '· مشترک'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {saving ? (
        <div className="flex items-center gap-1">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="نام نگاشت" className="h-8 text-sm w-40" />
          <Button size="sm" className="h-8" disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            ذخیره
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setSaving(false)}>انصراف</Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setSaving(true)}>
          <Save className="w-3.5 h-3.5" /> ذخیرهٔ نگاشت فعلی
        </Button>
      )}

      {presets.filter((p) => p.ownerId === user?.id).length > 0 && (
        <Select value="" onValueChange={(id) => deleteMutation.mutate(id)}>
          <SelectTrigger className="h-8 text-xs w-auto gap-1 text-destructive border-none shadow-none">
            <Trash2 className="w-3.5 h-3.5" />
          </SelectTrigger>
          <SelectContent>
            {presets.filter((p) => p.ownerId === user?.id).map((p) => (
              <SelectItem key={p.id} value={p.id}>حذف: {p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
